import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';

function resolveEntityCd(context: ChatFlowContext): string {
  const fd = context.formData || {};
  const tlink = fd.tlinkData || {};
  return String(
    context.entityCd
    || fd.entityCd
    || fd.icpCd
    || tlink.entityCd
    || tlink.icpCd
    || '',
  ).trim();
}

/** B32108 — Confirmation of Registrable Controllers (board declaration) */
export const confirmationControllersFlow: ChatFlow = {
  id: 'sg-controller-confirmation',
  taskCodeMatch: ['B32108'],
  categoryMatch: [
    'confirmation of registrable controllers',
    'confirmation registrable controller',
    'registrable controller confirmation',
    'confirm registrable controllers',
    'controller confirmation',
    'declaration of controllers',
  ],

  getInitialMessages(): ConversationMessage[] {
    return [
      {
        role: 'ai',
        content:
          'You selected **Confirmation of Registrable Controllers**.\n\n' +
          'Enter a **Task Title**, select the **Singapore company**, then on the right set the **confirmation date** and choose which **registrable controllers** to include.\n\n' +
          'Use **Add controller** or **Lookup from chat** on each row to pick controllers. Directors are used as **Signers** when the task is created.',
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **Singapore company** for this controller confirmation.',
      widget: 'entity-selector',
      widgetConfig: { targetVarKey: '_entity', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. Set the **confirmation date**, add or edit controllers on the right, then click **Create Task**.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a **registrable controller** for this entity.',
      widget: 'officer-list',
      widgetConfig: { targetVarKey: '_controller', targetEntityIndex: 0 },
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          { role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || ent.regNo || '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const fg = String(ctx.fieldGroup || '');
      const role = String(ctx.defaultRole || '').trim().toUpperCase();
      const isControllerLookup =
        fg === 'confirmationController' || role === 'CONTROLLER';
      if (!isControllerLookup) return {};

      const entityCd = resolveEntityCd(context);
      if (!entityCd) {
        return {
          messages: [
            {
              role: 'ai',
              content: 'Please select an entity in the chat first.',
              timestamp: Date.now(),
              stepId: 1,
              source: 'system',
            },
          ],
        };
      }

      return {
        messages: [
          {
            role: 'user',
            content: 'Looking up **registrable controller**...',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-list',
        nextStepWidgetData: {
          type: 'officer-list',
          entityCd,
          selectedRole: 'CONTROLLER',
        },
      };
    }

    if (
      eventName === 'officer-select'
      && (context.currentStepId === 'officer-list' || context.currentStepId === 'select-controller')
    ) {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected controller';
      return {
        messages: [
          {
            role: 'user',
            content: `Selected controller: **${officerName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
